import { toast } from 'sonner'
import { useEffect, useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { api } from '@/lib/api'
import { CopyButton } from '@/components/shared/CopyButton'
import { EmptyState } from '@/components/shared/EmptyState'
import type { ApiKey, Domain } from '@/types'
import {
  KeyRound,
  Plus,
  Trash2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Globe,
  Terminal,
  Code2,
  Pencil,
  Download,
  FileSpreadsheet,
  AlertTriangle,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.purpletoadmail.com'
const SCOPE_COLORS: Record<string, string> = {
  read: 'bg-blue-500/10 text-blue-400',
  send: 'bg-emerald-500/10 text-emerald-400',
  manage: 'bg-amber-500/10 text-amber-400',
  admin: 'bg-rose-500/10 text-rose-400',
}
const PIE_COLORS = ['#8b5cf6', '#22c55e', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899', '#06b6d4', '#6366f1']

interface PlanLimits {
  api_daily: number
  api_keys: number
}

interface UsageForm {
  name: string
  scopes: string[]
  restricted_domains: string[]
  allowed_ips: string
  rate_limit_daily: number
  test_mode: boolean
}

function downloadText(filename: string, content: string, type = 'text/plain') {
  const blob = new Blob([content], { type })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function requestsToCSV(rows: any[]) {
  const header = ['method', 'endpoint', 'status_code', 'response_ms', 'client_ip', 'created_at'].join(',')
  const lines = rows.map((r) =>
    [r.method, `"${r.endpoint}"`, r.status_code, r.response_ms ?? '', r.client_ip ?? '', r.created_at].join(',')
  )
  return [header, ...lines].join('\n')
}

function emptyUsageForm(maxDaily: number): UsageForm {
  return {
    name: '',
    scopes: ['read'],
    restricted_domains: [],
    allowed_ips: '',
    rate_limit_daily: maxDaily,
    test_mode: false,
  }
}

export function ApiKeys() {
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [domains, setDomains] = useState<Domain[]>([])
  const [planLimits, setPlanLimits] = useState<PlanLimits>({ api_daily: 1000, api_keys: 5 })
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)

  const [addOpen, setAddOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createdKey, setCreatedKey] = useState<string | null>(null)
  const [form, setForm] = useState<UsageForm>(emptyUsageForm(planLimits.api_daily))

  const [editKey, setEditKey] = useState<ApiKey | null>(null)
  const [editForm, setEditForm] = useState<UsageForm>(emptyUsageForm(planLimits.api_daily))
  const [updating, setUpdating] = useState(false)

  const [usageOpen, setUsageOpen] = useState(false)
  const [usageData, setUsageData] = useState<any>(null)
  const [usageLoading, setUsageLoading] = useState(false)

  const fetchKeys = async () => {
    setLoading(true)
    try {
      const res = await api.get(`/api-keys?page=${page}&per_page=20`)
      setKeys(res.data.keys || [])
      setTotal(res.data.pagination?.total || 0)
      setTotalPages(res.data.pagination?.total_pages || 1)
    } catch {
      toast.error('Failed to load API keys')
    } finally {
      setLoading(false)
    }
  }

  const fetchDomains = async () => {
    try {
      const res = await api.get('/domains')
      setDomains(res.data.domains || [])
    } catch {
      // non-fatal
    }
  }

  const fetchPlanLimits = async () => {
    try {
      const res = await api.get('/account/plan')
      const limits = res.data.limits || {}
      setPlanLimits({
        api_daily: limits.api_daily ?? 1000,
        api_keys: limits.api_keys ?? 5,
      })
    } catch {
      // non-fatal
    }
  }

  useEffect(() => {
    fetchKeys()
  }, [page])

  useEffect(() => {
    fetchDomains()
    fetchPlanLimits()
  }, [])

  useEffect(() => {
    if (planLimits.api_daily && !addOpen && !editKey) {
      setForm((f) => ({ ...f, rate_limit_daily: Math.min(f.rate_limit_daily || planLimits.api_daily, planLimits.api_daily) }))
    }
  }, [planLimits.api_daily, addOpen, editKey])

  const activeKeys = useMemo(() => keys.filter((k) => !k.revoked).length, [keys])

  const toggleScope = (scope: string, current: string[], setter: (scopes: string[]) => void) => {
    if (current.includes(scope)) {
      setter(current.filter((s) => s !== scope))
    } else {
      setter([...current, scope])
    }
  }

  const normalizeIps = (raw: string) =>
    raw
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean)

  const handleCreate = async () => {
    if (!form.name.trim()) return
    setCreating(true)
    try {
      const payload = {
        ...form,
        allowed_ips: normalizeIps(form.allowed_ips),
      }
      const res = await api.post('/api-keys', payload)
      setCreatedKey(res.data.key)
      fetchKeys()
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to create key')
    } finally {
      setCreating(false)
    }
  }

  const openEdit = (key: ApiKey) => {
    setEditKey(key)
    setEditForm({
      name: key.name,
      scopes: key.scopes,
      restricted_domains: key.restricted_domains || [],
      allowed_ips: (key.allowed_ips || []).join('\n'),
      rate_limit_daily: key.rate_limit_daily,
      test_mode: key.test_mode,
    })
  }

  const handleUpdate = async () => {
    if (!editKey || !editForm.name.trim()) return
    setUpdating(true)
    try {
      await api.put(`/api-keys/${editKey.id}`, {
        ...editForm,
        allowed_ips: normalizeIps(editForm.allowed_ips),
      })
      toast.success('API key updated')
      setEditKey(null)
      fetchKeys()
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to update key')
    } finally {
      setUpdating(false)
    }
  }

  const handleRevoke = async (id: string) => {
    if (!confirm('Revoke this API key? This cannot be undone.')) return
    try {
      await api.delete(`/api-keys/${id}`)
      fetchKeys()
    } catch {
      toast.error('Failed to revoke key')
    }
  }

  const viewUsage = async (id: string) => {
    setUsageOpen(true)
    setUsageLoading(true)
    try {
      const res = await api.get(`/api-keys/${id}/usage`)
      setUsageData(res.data)
    } catch {
      setUsageData(null)
    } finally {
      setUsageLoading(false)
    }
  }

  const downloadEnv = (key: string) => {
    const content = `PURPLETOAD_API_URL=${API_BASE_URL}\nPURPLETOAD_API_KEY=${key}\n`
    downloadText('purpletoad.env', content)
    toast.success('.env file downloaded')
  }

  const exportUsageCSV = () => {
    if (!usageData?.recent_requests?.length) return
    const csv = requestsToCSV(usageData.recent_requests)
    downloadText(`api-key-usage-${usageData.api_key_id}.csv`, csv, 'text/csv')
    toast.success('Usage CSV downloaded')
  }

  const resetAddDialog = (open: boolean) => {
    setAddOpen(open)
    if (!open) {
      setCreatedKey(null)
      setForm(emptyUsageForm(planLimits.api_daily))
    }
  }

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 w-32 bg-accent rounded" />
        <div className="h-64 bg-accent rounded-xl" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">API Keys</h1>
          <p className="text-sm text-muted-foreground mt-1">Connect your application to PurpleToad Mail.</p>
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          disabled={activeKeys >= planLimits.api_keys}
          className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
        >
          <Plus className="mr-2 h-4 w-4" />
          New Key
        </Button>
      </div>

      {activeKeys >= planLimits.api_keys && (
        <div className="rounded-md bg-amber-500/10 p-3 text-amber-400 text-sm flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          You have reached the {planLimits.api_keys} active key limit for your plan.
        </div>
      )}

      {keys.length === 0 ? (
        <EmptyState
          title="No API keys yet"
          description="Create a key to send mail or manage resources through the API."
          icon={<KeyRound className="h-5 w-5" />}
          action={
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> New Key
            </Button>
          }
        />
      ) : (
        <>
          {/* CHECKPOINT: PRD-10 FR-10.5.3 — API base URL and code snippets for outbound send. */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Globe className="h-4 w-4 text-violet-400" />
                API Base URL
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-3 rounded-lg bg-accent/50 px-3 py-2">
                <code className="text-xs font-mono break-all">{API_BASE_URL}</code>
                <CopyButton text={API_BASE_URL} />
              </div>
              <Tabs defaultValue="curl">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="curl"><Terminal className="mr-1.5 h-3.5 w-3.5" /> cURL</TabsTrigger>
                  <TabsTrigger value="python"><Code2 className="mr-1.5 h-3.5 w-3.5" /> Python</TabsTrigger>
                  <TabsTrigger value="javascript"><Code2 className="mr-1.5 h-3.5 w-3.5" /> JS</TabsTrigger>
                </TabsList>
                <TabsContent value="curl" className="mt-3">
                  <div className="relative rounded-lg bg-accent/50 p-3">
                    <pre className="text-xs font-mono whitespace-pre-wrap break-all overflow-x-auto">
{`curl -X POST ${API_BASE_URL}/api/v1/outbound/send \\
  -H "Authorization: Bearer <YOUR_API_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '{"from_email":"you@example.com","to":["recipient@example.com"],"subject":"Hello","text":"Hello from PurpleToad Mail"}'`}
                    </pre>
                    <div className="absolute top-2 right-2">
                      <CopyButton text={`curl -X POST ${API_BASE_URL}/api/v1/outbound/send \\
  -H "Authorization: Bearer <YOUR_API_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '{"from_email":"you@example.com","to":["recipient@example.com"],"subject":"Hello","text":"Hello from PurpleToad Mail"}'`} />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="python" className="mt-3">
                  <div className="relative rounded-lg bg-accent/50 p-3">
                    <pre className="text-xs font-mono whitespace-pre-wrap break-all overflow-x-auto">
{`import requests

resp = requests.post(
    "${API_BASE_URL}/api/v1/outbound/send",
    headers={"Authorization": "Bearer <YOUR_API_KEY>"},
    json={
        "from_email": "you@example.com",
        "to": ["recipient@example.com"],
        "subject": "Hello",
        "text": "Hello from PurpleToad Mail",
    },
)
print(resp.json())`}
                    </pre>
                    <div className="absolute top-2 right-2">
                      <CopyButton text={`import requests\n\nresp = requests.post(\n    "${API_BASE_URL}/api/v1/outbound/send",\n    headers={"Authorization": "Bearer <YOUR_API_KEY>"},\n    json={\n        "from_email": "you@example.com",\n        "to": ["recipient@example.com"],\n        "subject": "Hello",\n        "text": "Hello from PurpleToad Mail",\n    },\n)\nprint(resp.json())`} />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="javascript" className="mt-3">
                  <div className="relative rounded-lg bg-accent/50 p-3">
                    <pre className="text-xs font-mono whitespace-pre-wrap break-all overflow-x-auto">
{`const resp = await fetch("${API_BASE_URL}/api/v1/outbound/send", {
  method: "POST",
  headers: {
    "Authorization": "Bearer <YOUR_API_KEY>",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    from_email: "you@example.com",
    to: ["recipient@example.com"],
    subject: "Hello",
    text: "Hello from PurpleToad Mail",
  }),
});
const data = await resp.json();
console.log(data);`}
                    </pre>
                    <div className="absolute top-2 right-2">
                      <CopyButton text={`const resp = await fetch("${API_BASE_URL}/api/v1/outbound/send", {\n  method: "POST",\n  headers: {\n    "Authorization": "Bearer <YOUR_API_KEY>",\n    "Content-Type": "application/json",\n  },\n  body: JSON.stringify({\n    from_email: "you@example.com",\n    to: ["recipient@example.com"],\n    subject: "Hello",\n    text: "Hello from PurpleToad Mail",\n  }),\n});\nconst data = await resp.json();\nconsole.log(data);`} />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <div className="grid gap-3">
            {keys.map((key) => (
              <Card key={key.id} className="hover:bg-accent/20 transition-colors">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center shrink-0">
                      <KeyRound className="h-5 w-5 text-amber-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold">{key.name}</p>
                        {key.rotation_warning && (
                          <span className="text-[10px] text-amber-400 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            {key.age_days} days old — consider rotation
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-xs font-mono text-muted-foreground">{key.key_prefix}****</span>
                        <span className="flex gap-1">
                          {key.scopes.map((s) => (
                            <span key={s} className={`text-[10px] px-1.5 py-0.5 rounded ${SCOPE_COLORS[s] || 'bg-accent text-muted-foreground'}`}>
                              {s}
                            </span>
                          ))}
                        </span>
                        {key.test_mode && <span className="text-[10px] text-amber-400 border border-amber-400/30 px-1.5 py-0.5 rounded">TEST</span>}
                        {key.revoked && <span className="text-[10px] text-destructive border border-destructive/30 px-1.5 py-0.5 rounded">REVOKED</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                        <span>{key.request_count_total.toLocaleString()} requests</span>
                        {key.restricted_domains && key.restricted_domains.length > 0 && (
                          <span>{key.restricted_domains.length} domain restriction(s)</span>
                        )}
                        {key.allowed_ips && key.allowed_ips.length > 0 && (
                          <span>{key.allowed_ips.length} IP allowlist rule(s)</span>
                        )}
                        <span>{key.rate_limit_daily.toLocaleString()}/day</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!key.revoked && (
                      <>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => viewUsage(key.id)} title="View usage">
                          <BarChart3 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(key)} title="Edit restrictions">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleRevoke(key.id)} disabled={key.revoked}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">{total} total</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">Page {page}</span>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Create Key Dialog */}
      <Dialog open={addOpen} onOpenChange={resetAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{createdKey ? 'API Key Created' : 'Create API Key'}</DialogTitle>
          </DialogHeader>
          {!createdKey ? (
            <div className="space-y-4 py-2">
              {/* CHECKPOINT: PRD-10 FR-10.5.2 — Create key form with scopes, restrictions, rate limit, test mode. */}
              <div>
                <Label htmlFor="key-name">Name</Label>
                <Input
                  id="key-name"
                  placeholder="e.g. Production Agent"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Scopes</Label>
                <div className="flex flex-wrap gap-3 mt-1.5">
                  {['read', 'send', 'manage', 'bulk:send', 'bulk:read', 'bulk:manage'].map((scope) => (
                    <div key={scope} className="flex items-center gap-1.5">
                      <Checkbox
                        id={`scope-${scope}`}
                        checked={form.scopes.includes(scope)}
                        onCheckedChange={() => toggleScope(scope, form.scopes, (scopes) => setForm({ ...form, scopes }))}
                      />
                      <Label htmlFor={`scope-${scope}`} className="text-sm font-normal capitalize">{scope}</Label>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label>Domain restriction</Label>
                {domains.length === 0 ? (
                  <p className="text-xs text-muted-foreground mt-1.5">No domains available.</p>
                ) : (
                  <div className="mt-1.5 max-h-32 overflow-y-auto rounded-md border border-input p-2 space-y-1.5">
                    {domains.map((d) => (
                      <div key={d.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`domain-${d.id}`}
                          checked={form.restricted_domains.includes(d.id)}
                          onCheckedChange={(checked) => {
                            const next = checked
                              ? [...form.restricted_domains, d.id]
                              : form.restricted_domains.filter((id) => id !== d.id)
                            setForm({ ...form, restricted_domains: next })
                          }}
                        />
                        <Label htmlFor={`domain-${d.id}`} className="text-sm font-normal">{d.name}</Label>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Restricted keys can only act on the selected domains. Leave empty for all domains.
                </p>
              </div>
              <div>
                <Label htmlFor="allowed-ips">IP allowlist</Label>
                <Textarea
                  id="allowed-ips"
                  rows={3}
                  className="mt-1.5"
                  placeholder="1.2.3.4&#10;10.0.0.0/8"
                  value={form.allowed_ips}
                  onChange={(e) => setForm({ ...form, allowed_ips: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">One IP or CIDR per line or comma-separated. Leave blank for any IP.</p>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Daily rate limit</Label>
                  <span className="text-xs font-mono">{form.rate_limit_daily.toLocaleString()} / day</span>
                </div>
                <Slider
                  value={[form.rate_limit_daily]}
                  min={50}
                  max={planLimits.api_daily}
                  step={50}
                  onValueChange={([v]) => setForm({ ...form, rate_limit_daily: v })}
                  className="mt-2"
                />
                <p className="text-xs text-muted-foreground mt-1">Plan max: {planLimits.api_daily.toLocaleString()} / day</p>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="test-mode"
                  checked={form.test_mode}
                  onCheckedChange={(checked) => setForm({ ...form, test_mode: checked === true })}
                />
                <Label htmlFor="test-mode" className="text-sm font-normal">Test mode</Label>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2">
                <p className="text-sm text-emerald-400 font-medium">API Key Created</p>
                <p className="text-xs text-muted-foreground mt-1">This key will only be shown once. Copy it now.</p>
              </div>
              <div className="bg-accent/50 rounded-lg px-3 py-2 flex items-center justify-between gap-3">
                <code className="text-xs font-mono break-all">{createdKey}</code>
                <CopyButton text={createdKey} />
              </div>
              <Button variant="outline" size="sm" className="w-full" onClick={() => downloadEnv(createdKey)}>
                <Download className="mr-2 h-4 w-4" />
                Download .env file
              </Button>
            </div>
          )}
          <DialogFooter>
            {!createdKey ? (
              <>
                <Button variant="outline" onClick={() => resetAddDialog(false)}>Cancel</Button>
                <Button onClick={handleCreate} disabled={creating || !form.name.trim()}>
                  {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create Key
                </Button>
              </>
            ) : (
              <Button onClick={() => resetAddDialog(false)}>Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Key Dialog */}
      <Dialog open={!!editKey} onOpenChange={(open) => !open && setEditKey(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit API Key</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Scopes</Label>
              <div className="flex flex-wrap gap-3 mt-1.5">
                {['read', 'send', 'manage', 'bulk:send', 'bulk:read', 'bulk:manage'].map((scope) => (
                  <div key={scope} className="flex items-center gap-1.5">
                    <Checkbox
                      id={`edit-scope-${scope}`}
                      checked={editForm.scopes.includes(scope)}
                      onCheckedChange={() => toggleScope(scope, editForm.scopes, (scopes) => setEditForm({ ...editForm, scopes }))}
                    />
                    <Label htmlFor={`edit-scope-${scope}`} className="text-sm font-normal capitalize">{scope}</Label>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Domain restriction</Label>
              {domains.length === 0 ? (
                <p className="text-xs text-muted-foreground mt-1.5">No domains available.</p>
              ) : (
                <div className="mt-1.5 max-h-32 overflow-y-auto rounded-md border border-input p-2 space-y-1.5">
                  {domains.map((d) => (
                    <div key={d.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`edit-domain-${d.id}`}
                        checked={editForm.restricted_domains.includes(d.id)}
                        onCheckedChange={(checked) => {
                          const next = checked
                            ? [...editForm.restricted_domains, d.id]
                            : editForm.restricted_domains.filter((id) => id !== d.id)
                          setEditForm({ ...editForm, restricted_domains: next })
                        }}
                      />
                      <Label htmlFor={`edit-domain-${d.id}`} className="text-sm font-normal">{d.name}</Label>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="edit-allowed-ips">IP allowlist</Label>
              <Textarea
                id="edit-allowed-ips"
                rows={3}
                className="mt-1.5"
                placeholder="1.2.3.4&#10;10.0.0.0/8"
                value={editForm.allowed_ips}
                onChange={(e) => setEditForm({ ...editForm, allowed_ips: e.target.value })}
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Daily rate limit</Label>
                <span className="text-xs font-mono">{editForm.rate_limit_daily.toLocaleString()} / day</span>
              </div>
              <Slider
                value={[editForm.rate_limit_daily]}
                min={50}
                max={planLimits.api_daily}
                step={50}
                onValueChange={([v]) => setEditForm({ ...editForm, rate_limit_daily: v })}
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">Plan max: {planLimits.api_daily.toLocaleString()} / day</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditKey(null)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={updating || !editForm.name.trim()}>
              {updating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Usage Dialog */}
      <Dialog open={usageOpen} onOpenChange={(open) => { if (!open) { setUsageOpen(false); setUsageData(null) } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>API Key Usage</DialogTitle>
          </DialogHeader>
          {usageLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
            </div>
          ) : usageData ? (
            <div className="space-y-6 py-2">
              {/* CHECKPOINT: PRD-10 FR-10.5.4 — Usage summary and charts. */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div className="bg-accent/30 rounded-lg px-3 py-2">
                  <p className="text-muted-foreground text-xs">Total Requests</p>
                  <p className="text-lg font-semibold">{(usageData.summary?.requests_total ?? 0).toLocaleString()}</p>
                </div>
                <div className="bg-accent/30 rounded-lg px-3 py-2">
                  <p className="text-muted-foreground text-xs">Success Rate</p>
                  <p className="text-lg font-semibold">{((usageData.summary?.success_rate ?? 0) * 100).toFixed(1)}%</p>
                </div>
                <div className="bg-accent/30 rounded-lg px-3 py-2">
                  <p className="text-muted-foreground text-xs">Avg Response</p>
                  <p className="text-lg font-semibold">{usageData.summary?.avg_response_ms ?? 0} ms</p>
                </div>
                <div className="bg-accent/30 rounded-lg px-3 py-2">
                  <p className="text-muted-foreground text-xs">Period</p>
                  <p className="text-xs font-medium mt-1">{usageData.period?.from} → {usageData.period?.to}</p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Daily requests</p>
                <div className="h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={usageData.daily_breakdown || []}>
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={50} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ backgroundColor: '#14141f', border: '1px solid #2a2a3a' }} />
                      <Bar dataKey="requests_total" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium mb-2">Endpoint breakdown</p>
                  <div className="h-56 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={usageData.top_endpoints || []}
                          dataKey="count"
                          nameKey="endpoint"
                          outerRadius={70}
                          label={(entry: { endpoint: string }) => entry.endpoint}
                        >
                          {(usageData.top_endpoints || []).map((_: any, i: number) => (
                            <Cell key={`cell-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: '#14141f', border: '1px solid #2a2a3a' }} />
                        <Legend wrapperStyle={{ fontSize: 10 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium mb-2">Requests by scope</p>
                  <div className="space-y-2">
                    {Object.entries(usageData.summary?.requests_by_scope || {}).map(([scope, count]: [string, any]) => (
                      <div key={scope} className="flex items-center justify-between text-sm">
                        <span className="capitalize">{scope}</span>
                        <span className="font-mono">{Number(count).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">Recent requests</p>
                  <Button variant="outline" size="sm" onClick={exportUsageCSV} disabled={!usageData.recent_requests?.length}>
                    <FileSpreadsheet className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                </div>
                {usageData.recent_requests?.length ? (
                  <div className="rounded-md border overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-accent/50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Method</th>
                          <th className="px-3 py-2 text-left font-medium">Endpoint</th>
                          <th className="px-3 py-2 text-left font-medium">Status</th>
                          <th className="px-3 py-2 text-left font-medium">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usageData.recent_requests.map((r: any, idx: number) => (
                          <tr key={idx} className="border-t">
                            <td className="px-3 py-2 font-mono">{r.method}</td>
                            <td className="px-3 py-2 truncate max-w-[200px]" title={r.endpoint}>{r.endpoint}</td>
                            <td className="px-3 py-2">
                              <span className={`px-1.5 py-0.5 rounded ${r.status_code >= 200 && r.status_code < 300 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'}`}>
                                {r.status_code}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No recent requests.</p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No usage data available.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
